import { useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { ReducerState } from '@/services/common/lib/reducerState';
import { useEffect, useCallback, useState } from 'react';
import { useToast } from '@/components/toast/useToast';
import useRetryRequest from '@/pages/modals/components/useRetryRequest';
import { LobbyTableOpenBuyInAction } from '@/services/lobby/lobbyActions';
import {
  useAccountAbstractionStore,
  useAccountAbstractionDispatch,
} from '@/features/account-abstraction/store/AccountAbstractionContext';
import {
  setLoadingTransaction,
  deleteRebuySession,
} from '@/features/account-abstraction/store/accountAbstractionActions';
import { rebuyTableContract } from '@/pages/table/web3/rebuyTable';
import { isSessionError } from '@/features/account-abstraction/lib/isSessionError';
import { autoTopUp } from '@/services/table/tableActions';

const useAutoTopUp = () => {
  const { tableId } = useParams();
  const { toast } = useToast();

  const [shouldRetryRebuyRequest, setShouldRetryRebuyRequest] = useState(false);

  const dispatch = useDispatch();

  const {
    playerSeatStatus,
    playerSeatAutoTopUpThreshold,
    playerSeatAutoTopUpBuyInAmount,
    playerSeatStack,
    tableMinBuyIn,
    tableUrl,
    tableIsRealMoney,
    tableContractId,
    playerSeatAutoTopUpEnabled,
    playerSeatSeat,
    playerId,
    playerSeatWaiting,
  } = useSelector((state: ReducerState) => {
    const table = state.tables[tableId as string] || null;

    return {
      playerSeatStatus: table?.playerSeat?.status,
      playerSeatSeat: table?.playerSeat?.seat,
      playerSeatWaiting: table?.playerSeat?.waiting,
      playerSeatAutoTopUpEnabled: table?.playerSeat?.autoTopup,
      playerSeatAutoTopUpThreshold: table?.playerSeat?.autoTopUpThreshold || 0,
      playerSeatAutoTopUpBuyInAmount: table?.playerSeat?.autoTopUpBuyIn,
      playerSeatStack: table?.playerSeat?.totalStack
        ? table?.playerSeat?.totalStack
        : (table?.playerSeat?.stack as number),
      tableMinBuyIn: table?.minBuyin,
      tableUrl: table?.url,
      tableIsRealMoney: table?.isRealMoney,
      tableContractId: table?.contractId,
      playerSeatUserId: table?.playerSeat?.userId,
      playerId: state.auth.id,
    };
  });

  const accountAbstractionStore = useAccountAbstractionStore();
  const accountAbstrationDispatch = useAccountAbstractionDispatch();

  const rebuy = useCallback(async () => {
    if (!tableId) {
      alert('You are not joining to a valid table');
      return;
    }
    try {
      if (!playerId) throw new Error('No playerId');

      const amountNeededToTopUp = (playerSeatAutoTopUpBuyInAmount || tableMinBuyIn) - (playerSeatStack || 0);

      accountAbstrationDispatch(setLoadingTransaction(true));

      let txId;

      if (tableIsRealMoney) {
        if (!tableContractId) throw new Error('No contractId');

        const result = await rebuyTableContract(
          accountAbstractionStore.state,
          BigInt(tableContractId),
          String(amountNeededToTopUp),
        );

        txId = result?.transactionHash;
      }

      dispatch(
        autoTopUp({
          tableId,
          amount: amountNeededToTopUp,
          txId,
          tableUrl,
        }),
      );
    } catch (err: unknown) {
      if (err instanceof Error) {
        if (isSessionError(err.message)) {
          accountAbstrationDispatch(deleteRebuySession());
          setShouldRetryRebuyRequest(true);
        } else
          toast({
            description: `Error rebuying: ${err.message}`,
          });
      } else {
        toast({ description: 'Unknown error rebuying' });
      }
    } finally {
      accountAbstrationDispatch(setLoadingTransaction(false));
    }
  }, [
    tableId,
    tableUrl,
    tableContractId,
    tableIsRealMoney,
    playerId,
    playerSeatAutoTopUpBuyInAmount,
    tableMinBuyIn,
    playerSeatStack,
  ]);

  useEffect(() => {
    if (!tableId || playerSeatWaiting) {
      return;
    }

    // only process once the seat status is false
    if (playerSeatStatus === false) {
      if (playerSeatAutoTopUpEnabled && playerSeatStack <= playerSeatAutoTopUpThreshold) {
        rebuy();
        return;
      }

      if (!playerSeatAutoTopUpEnabled && playerSeatStack <= 0) {
        dispatch(LobbyTableOpenBuyInAction(tableId, playerSeatSeat, 0, true, true));
      }
    }
  }, [
    playerSeatStatus,
    playerSeatSeat,
    playerSeatStack,
    playerSeatAutoTopUpEnabled,
    playerSeatAutoTopUpThreshold,
    playerSeatAutoTopUpBuyInAmount,
    tableId,
    tableUrl,
    tableContractId,
    playerSeatWaiting,
    tableIsRealMoney,
    playerId,
    tableMinBuyIn,
  ]);

  useRetryRequest(
    rebuy,
    [],
    accountAbstractionStore?.state?.joinTableBatchSession &&
      !accountAbstractionStore?.state?.loadingJoinTableSession &&
      shouldRetryRebuyRequest,
    () => {
      setShouldRetryRebuyRequest(false);
    },
  );
};

export default useAutoTopUp;
